'use client';
import classNames from 'classnames';
import { useLocale, useTranslations } from 'next-intl';
import React, { useEffect, useState } from 'react';

import { Icon } from '@components/icons';
import PauseSVG from 'public/static/svg/speech-pause.svg';

type SpeechStatusType = 'SPEAKING' | 'PAUSED' | 'IDLE';
type SpeechIconType = Record<SpeechStatusType, any>;

const speechIcon: SpeechIconType = {
  SPEAKING: <Icon icon="audio" size="18" />,
  PAUSED: <PauseSVG />,
  IDLE: <Icon icon="audio" size="18" />,
};

function SpeechSynthesisController({ textToSpeech }: { textToSpeech?: string }) {
  const t = useTranslations('main');
  const lang = useLocale();

  const [speechStatus, setSpeechStatus] = useState('IDLE');
  const [voice, setVoice] = useState<any>();
  const locale = `${lang}-${lang.toUpperCase()}`;

  useEffect(() => {
    if (!window.speechSynthesis) {
      return;
    }
    const listener = () => {
      const voice = speechSynthesis.getVoices().find((voice) => voice.lang === locale || voice.lang === lang);
      setVoice(voice);
    };
    window.speechSynthesis.onvoiceschanged = listener;

    setTimeout(() => {
      // fallback for firefox
      listener();
    }, 1000);

    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, []);

  const startSpeaking = () => {
    const utterance = new SpeechSynthesisUtterance(
      textToSpeech
        ?.replace(/(<([^>]+)>)/gi, '')
        .replace(/[-–—]/g, '')
        .replace(/\n/g, ' ')
        .trim(),
    );
    if (speechSynthesis.speaking) {
      if (speechSynthesis.paused) {
        setSpeechStatus('PAUSED');
        speechSynthesis.resume();
        return;
      }
      setSpeechStatus('SPEAKING');
      speechSynthesis.pause();
      return;
    } else {
      if (speechSynthesis.getVoices().length === 0) {
        alert(t('posts.speechSynthesisImpossible'));
        return;
      }
    }
    if (voice) {
      utterance.voice = voice;
    }

    utterance.lang = `${lang}-${lang.toUpperCase()}`;
    utterance.onend = () => {
      setSpeechStatus('IDLE');
    };
    setSpeechStatus('PAUSED');
    speechSynthesis.speak(utterance);
  };

  return (
    <div
      id="speech-synthesis-controller"
      className="flex flex-row items-center gap-2 rounded-full bg-premium-900 px-5 py-3 text-[0.875rem] font-[600] text-white lg:mb-0 dark:bg-premium-dark"
      onClick={startSpeaking}
    >
      <div className={classNames(['h-[16px] w-[16px]'])}>{speechIcon[speechStatus]}</div>
      <p className="mb-0 whitespace-nowrap">{t('posts.speechSynthesis')}</p>
    </div>
  );
}

export default SpeechSynthesisController;
